import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { useBranding } from "@frontend/contexts/BrandingContext";
import CheckroomRoundedIcon from "@mui/icons-material/CheckroomRounded";
import { useState } from "react";

interface BrandMarkProps {
	/** Square mark size in px. */
	size?: number;
	/** Show the app name next to the mark. */
	withName?: boolean;
}

/**
 * The retailer's logo (or the default mark — a hanger on the brand colour,
 * same as the generated favicon — when no logo is set / it fails to load), optionally followed by the app name. Wide logos keep their aspect
 * ratio inside the mark's height and hide the name (it is usually part of
 * the artwork); square marks show the name beside them.
 */
export function BrandMark({ size = 36, withName = true }: BrandMarkProps) {
	const branding = useBranding();
	const [broken, setBroken] = useState(false);
	// A wide logo usually carries the name already; a square mark does not.
	const [wideLogo, setWideLogo] = useState(false);
	const logo = !broken ? (branding.logoUrl ?? branding.iconUrl) : null;

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
			{logo ? (
				<Box
					component="img"
					src={logo}
					alt={branding.appName}
					onError={() => setBroken(true)}
					onLoad={(event) => {
						const { naturalWidth, naturalHeight } = event.currentTarget;
						setWideLogo(naturalHeight > 0 && naturalWidth / naturalHeight > 1.4);
					}}
					sx={{ height: size, maxWidth: size * 3, objectFit: "contain", display: "block", flexShrink: 0 }}
				/>
			) : (
				<Box
					aria-hidden
					sx={{
						width: size,
						height: size,
						borderRadius: `${Math.round(size * 0.32)}px`,
						flexShrink: 0,
						display: "grid",
						placeItems: "center",
						color: "primary.contrastText",
						background: (theme) =>
							`linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
					}}
				>
					<CheckroomRoundedIcon sx={{ fontSize: size * 0.6 }} />
				</Box>
			)}
			{withName && !wideLogo && (
				<Typography variant="h6" noWrap sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
					{branding.appName}
				</Typography>
			)}
		</Box>
	);
}
